import * as React from 'react';
import { cn } from '../../utils';

const colorMap = {
  pink: 'var(--riso-pink)',
  teal: 'var(--riso-teal)',
  amber: 'var(--riso-amber)',
  blue: 'var(--riso-blue)',
  black: 'var(--foreground)',
} as const;

export interface RotatingBadgeLabels {
  circularText: string;
}

const DEFAULT_LABELS: RotatingBadgeLabels = {
  circularText: 'SWISS MADE \u00b7 CONFORME \u00b7 SIMPLE \u00b7 ',
};

export interface RotatingBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: number;
  color?: keyof typeof colorMap;
  duration?: number;
  labels?: Partial<RotatingBadgeLabels>;
  children?: React.ReactNode;
}

const RotatingBadge = React.forwardRef<HTMLDivElement, RotatingBadgeProps>(
  (
    {
      size = 160,
      color = 'blue',
      duration = 20,
      labels,
      children,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const id = React.useId();
    const pathId = `rotating-badge-path-${id}`;
    const mergedLabels = { ...DEFAULT_LABELS, ...labels };

    return (
      <div
        ref={ref}
        aria-hidden
        className={cn('pointer-events-none relative', className)}
        style={{ width: size, height: size, ...style }}
        {...props}
      >
        <div
          className="h-full w-full"
          style={{ animation: `rotate ${duration}s linear infinite` }}
        >
          <svg viewBox="0 0 200 200" className="h-full w-full">
            <path
              id={pathId}
              d="M100,100 m-75,0 a75,75 0 1,1 150,0 a75,75 0 1,1 -150,0"
              fill="none"
            />
            <text
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '14px',
                fill: colorMap[color],
                letterSpacing: '6px',
              }}
            >
              <textPath href={`#${pathId}`}>
                {mergedLabels.circularText}
              </textPath>
            </text>
          </svg>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          {children ?? <span className="text-3xl">🇨🇭</span>}
        </div>
      </div>
    );
  },
);
RotatingBadge.displayName = 'RotatingBadge';

export { RotatingBadge };
